import type { Dispatch } from 'react';
import { useTranslation } from 'react-i18next';

import Warning from '@/assets/icons/warning.svg?component';
import { Alert } from '@/components/atoms/Alert';
import { Badge } from '@/components/atoms/Badge';
import { Card } from '@/components/atoms/Card';
import { Overline } from '@/components/atoms/Eyebrow';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import { useServerErrorMessage } from '@/components/organisms/FishGroupForms';
import type { OverviewCage } from '@/domain/fishGroups';

import {
    type CageWarning,
    inferOperationType,
    type WizardOperationType,
} from '../operateWizardHelpers';
import type {
    OperateWizardAction,
    OperateWizardState,
    OperationKind,
} from '../operateWizardReducer';

import { ConfirmDestinationsSection } from './confirm/ConfirmDestinationsSection';
import { ConfirmMetadataFields } from './confirm/ConfirmMetadataFields';

import styles from '../operateModals.module.scss';

interface Props {
    state: OperateWizardState;
    locationCages: OverviewCage[];
    cageWarnings: CageWarning[];
    submitError: unknown;
    /**
     * Earliest selectable operation date. Set by the container as the max
     * `lifecycleStartedAt` over every source so the picker can't land before
     * any source FG existed. The backend's stricter check (against the
     * source's current location `fromDate`) catches the remaining edge of
     * a relocated source via `INVALID_OPERATION_DATE`.
     */
    operationMinDate: Date | null;
    dispatch: Dispatch<OperateWizardAction>;
}

/**
 * Final step of the wizard: a human-readable summary of what's about to
 * happen plus the optional operational metadata (date, reason, notes).
 *
 * The chosen UI mode and the **inferred backend operation type** are both
 * surfaced — the latter is informational, useful for the audit trail and
 * to let the operator double-check the system's interpretation. They will
 * usually match, but for example if the operator is in split mode and
 * accidentally puts every destination in occupied cages, the inferred
 * type will reveal itself as a `redistribution`.
 */
export default function ConfirmStep(props: Props) {
    const { state, locationCages, cageWarnings, submitError, operationMinDate, dispatch } = props;
    const { t } = useTranslation('fishGroups');
    const serverErrorMessage = useServerErrorMessage(submitError);

    const cageNameById = new Map(locationCages.map((c) => [c.cageId, c.cageName]));
    // Bioscope ids per destination cage — surfaced in the destinations list
    // so the operator can verify *where* the new FG will be observed before
    // committing. An empty array means the cage has no active bioscope, in
    // which case we still surface that fact explicitly (instead of staying
    // silent) so "no observation possible" is a conscious confirmation.
    const cageBioscopesById = new Map(locationCages.map((c) => [c.cageId, c.activeBioscopes]));
    const sourceNameById = new Map(state.sources.map((s) => [s.fishGroupId, s.name]));
    const sourceCageNameById = new Map(
        state.sources.map((s) => [
            s.fishGroupId,
            s.currentCageId != null ? (cageNameById.get(s.currentCageId) ?? '') : '',
        ])
    );

    const inferredBackendType: WizardOperationType = inferOperationType({
        sourceCount: state.sources.length,
        destinations: state.destinations.map((d) => ({ receivesFrom: d.receivesFrom })),
    });

    // In Split mode, the destination that lands in the source's cage is the
    // "remainder" — the new fish group that keeps the part of the population
    // that stays behind. We surface a small tag in the destinations list so
    // the operator can tell it apart from the target destinations at a glance.
    const initialSource = state.sources.find((s) => s.fishGroupId === state.initialSourceId);
    const remainderCageId =
        state.operationKind === 'split' && initialSource?.currentCageId != null
            ? initialSource.currentCageId
            : null;

    // Resolve the "from" cage for each planned bioscope move so the summary
    // reads "Bioscope X: Cage A → Cage B" without the operator having to
    // remember the source cage. We scan locationCages for the cage that
    // currently lists the bioscope as active.
    const bioscopeMovementsSummary = state.bioscopeMovements.map((m) => {
        const sourceCage = locationCages.find((c) => c.activeBioscopes.includes(m.bioscopeId));
        return {
            bioscopeId: m.bioscopeId,
            fromCageName: sourceCage?.cageName ?? '',
            toCageName: cageNameById.get(m.targetCageId) ?? '',
        };
    });

    return (
        <section className={styles.body}>
            <div>
                <h4 className={styles.heading}>{t('actions.operate.confirm.heading')}</h4>
                <p className={styles.description}>{t('actions.operate.confirm.description')}</p>
            </div>

            <Card as="article" radius="sm" padding="xs" className={styles.confirmSection}>
                <h5 className={styles.confirmSectionTitle}>
                    {t('actions.operate.confirm.modeSection')}
                </h5>
                <dl className={styles.summaryGrid}>
                    <Overline as="dt">{t('actions.operate.confirm.modeLabel')}</Overline>
                    <dd>{state.operationKind ? labelForKind(state.operationKind, t) : '—'}</dd>
                    <Overline as="dt">{t('actions.operate.confirm.backendTypeLabel')}</Overline>
                    <dd title={t(`actions.operate.confirm.backendType.${inferredBackendType}`)}>
                        {labelForKind(inferredBackendType, t)}
                        <Badge className={styles.backendType}>
                            {t(`actions.operate.confirm.backendType.${inferredBackendType}`)}
                        </Badge>
                    </dd>
                </dl>
            </Card>

            <Card as="article" radius="sm" padding="xs" className={styles.confirmSection}>
                <h5 className={styles.confirmSectionTitle}>
                    {t('actions.operate.confirm.sourcesSection', { count: state.sources.length })}
                </h5>
                <ul className={styles.summaryList}>
                    {state.sources.map((s) => {
                        const cageName = sourceCageNameById.get(s.fishGroupId);
                        return (
                            <li key={s.fishGroupId}>
                                <span className={styles.summaryName}>{s.name}</span>
                                {cageName && (
                                    <span className={styles.summaryMeta}>({cageName})</span>
                                )}
                            </li>
                        );
                    })}
                </ul>
            </Card>
            <ConfirmDestinationsSection
                destinations={state.destinations}
                cageNameById={cageNameById}
                cageBioscopesById={cageBioscopesById}
                sourceNameById={sourceNameById}
                remainderCageId={remainderCageId}
            />

            {bioscopeMovementsSummary.length > 0 && (
                <Card as="article" radius="sm" padding="xs" className={styles.confirmSection}>
                    <h5 className={styles.confirmSectionTitle}>
                        {t('actions.operate.confirm.bioscopeMovementsSection', {
                            count: bioscopeMovementsSummary.length,
                        })}
                    </h5>
                    <ul className={styles.summaryList}>
                        {bioscopeMovementsSummary.map((m) => (
                            <li key={m.bioscopeId}>
                                <span className={styles.summaryName}>
                                    {t('actions.operate.confirm.bioscopeMovementItem', {
                                        id: m.bioscopeId,
                                        fromCage: m.fromCageName,
                                        toCage: m.toCageName,
                                    })}
                                </span>
                            </li>
                        ))}
                    </ul>
                </Card>
            )}

            {cageWarnings.length > 0 && (
                // A warning, not an error: an unlisted group in a target cage does not block saving.
                <Alert
                    tone="warning"
                    size="small"
                    icon={<SvgIcon icon={Warning} size="small" color="currentColor" />}
                    title={t('actions.operate.confirm.warnings.heading')}>
                    <ul className={styles.errorList}>
                        {cageWarnings.map((w) => (
                            <li key={`${w.destinationTempId}-${w.cageId}`}>
                                {t('actions.operate.confirm.warnings.cageOccupiedByUnlisted', {
                                    cageName: w.cageName,
                                    fishGroupName: w.unlistedFishGroupName,
                                })}
                            </li>
                        ))}
                    </ul>
                </Alert>
            )}

            <ConfirmMetadataFields
                state={state}
                operationMinDate={operationMinDate}
                dispatch={dispatch}
            />

            {serverErrorMessage && (
                <Alert tone="danger" size="small">
                    {serverErrorMessage}
                </Alert>
            )}
        </section>
    );
}

function labelForKind(
    kind: OperationKind,
    t: (key: string, options?: Record<string, unknown>) => string
): string {
    return t(`actions.operate.kind.cards.${kind}.title`);
}
